import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuestionCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button, Menu } from 'antd';
import Modal from 'antd/lib/modal/Modal';

import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { getTokenExpirationDate } from '@totem/components/userContext/accountUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { secondsToDuration } from '@totem/utilities/timeUtilities';

const TokenExpirationMonitor = () => {
  const navigate = useNavigate();
  const { loading, getSoftApiToken, refreshToken, tokenRefreshCount } =
    useContext(UserProfileContext);
  const [expirationNear, setExpirationNear] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogCancelled, setDialogCancelled] = useState<boolean>(false);
  const [interval, setInterval] = useState<number>(2 * 60 * 1000);

  useEffect(() => {
    const token = getSoftApiToken();
    const expiration = getTokenExpirationDate(token);
    const now = new Date();
    const minutes = Math.abs(expiration.getTime() - now.getTime()) / 60000;
    setInterval(Math.max((minutes - 5) * 60 * 1000, 60 * 1000));

    setExpirationNear(false);

    setShowDialog(false);

    setDialogCancelled(false);
  }, [tokenRefreshCount]);

  const handleTokenRefresh = () => {
    setShowDialog(false);

    setDialogCancelled(true);

    refreshToken();
  };

  const runTokenMonitor = useCallback(() => {
    const token = getSoftApiToken();
    const expiration = getTokenExpirationDate(token);
    const now = new Date();
    const minutes = Math.abs(expiration.getTime() - now.getTime()) / 60000;
    if (expiration.getTime() < now.getTime()) {
      navigate('/login');
    }
    if (minutes < 5) {
      setExpirationNear(true);
    }
    if (minutes < 2 && !dialogCancelled) {
      setShowDialog(true);

      setInterval(Math.max(minutes * 60 * 1000, 5000));
    }
    if (minutes > 60 * 24 * 7) {
      handleTokenRefresh();
    }
  }, [loading, getSoftApiToken]);

  const getTimeToExpire = () => {
    const token = getSoftApiToken();
    const expiration = getTokenExpirationDate(token);
    const now = new Date();
    const seconds = Math.abs(expiration.getTime() - now.getTime()) / 1000;

    let ttl = secondsToDuration(seconds, true, false);
    if (ttl === '') {
      ttl = 'less than a minute';
    }
    return ttl;
  };

  useEffect(() => {
    const intervalId = window.setInterval(runTokenMonitor, interval);
    return () => window.clearInterval(intervalId);
  }, [runTokenMonitor, interval]);

  if (!expirationNear) {
    return <span />;
  }

  return (
    <>
      <Menu
        theme="light"
        mode="inline"
        onClick={() => setShowDialog((prevState) => !prevState)}
        items={[
          {
            key: 'session_expiring',
            title: 'Session Expiration',
            label: <span style={{ color: 'red' }}>Session Expiring</span>,
            icon: <SafetyOutlined />,
          },
        ]}
      />
      {showDialog && (
        <Modal
          title={
            <ModalTitle>
              <QuestionCircleOutlined />
              &nbsp;&nbsp;Extend Session?
            </ModalTitle>
          }
          onCancel={() => {
            setShowDialog(false);

            setDialogCancelled(true);
          }}
          open={showDialog && !dialogCancelled}
          footer={
            <ModalFooter>
              <Button
                onClick={() => {
                  setShowDialog(false);

                  setDialogCancelled(true);
                }}
              >
                No
              </Button>
              <Button
                type="primary"
                loading={false}
                onClick={handleTokenRefresh}
              >
                Yes, extend session.
              </Button>
            </ModalFooter>
          }
        >
          <div>
            Your authentication token will expire in {getTimeToExpire()}!
          </div>
        </Modal>
      )}
    </>
  );
};

export default TokenExpirationMonitor;
